import * as React from 'react';
import { ModalWindow } from '@cian/ui-kit/modal';
import { setCookie } from '@cian/utils/lib/browser/cookie';
import { useDispatch, useSelector } from 'react-redux';
import {
  trackAuthenticateMotivationClose,
  trackAuthenticateMotivationLinkClick,
  trackAuthenticateMotivationShow,
} from './tracking';
import { getRequestUrl } from './utils';
// eslint-disable-next-line import/no-restricted-paths
import { changeFavorite } from '../../../browser/services/favorite';
import { setFavorite } from '../../actions/newbuilding';
import { AuthenticateMotivation } from '../../components/Favorite/AuthenticateMotivation';
import { getGaLabel, getIsAuthenticated, getIsFavorite, getFullUrl } from '../../selectors/newbuilding';
import { IFavoriteActionTypes } from '../../types/analytics/favorite';
import { IFavoriteButtonProps, TFavoriteButtonTheme } from '../../types/newbuilding/favorite';
import { TThunkDispatch } from '../../types/redux/thunk';

interface IFavoriteContainerProps {
  button: React.ComponentType<IFavoriteButtonProps>;
  buttonTheme?: TFavoriteButtonTheme;
  eventActions?: IFavoriteActionTypes;
}

export function FavoriteContainer({ button: FavoriteButton, buttonTheme, eventActions }: IFavoriteContainerProps) {
  const [isMotivationPopupOpened, setMotivationPopupVisibility] = React.useState(false);
  const dispatch: TThunkDispatch = useDispatch();
  const isFavorite = useSelector(getIsFavorite);
  const isAuthenticated = useSelector(getIsAuthenticated);
  const path = useSelector(getFullUrl);
  const gaLabel = useSelector(getGaLabel);

  const motivateAuthenticate = React.useCallback(
    (e: React.MouseEvent) => {
      const cookie = typeof document !== 'undefined' && document.cookie.includes('shown_fav_popup');

      if (!isFavorite && !isAuthenticated && !cookie) {
        setMotivationPopupVisibility(true);
        trackAuthenticateMotivationShow(gaLabel);
        setCookie('shown_fav_popup', '1', { secure: false });

        return;
      }

      e.stopPropagation();
    },
    [isFavorite, isAuthenticated, gaLabel],
  );

  const onFavoriteButtonClick = React.useCallback(
    (e: React.MouseEvent) => {
      const newFavoriteState = !isFavorite;
      const { __header_updateFavorites__ } = window;

      if (__header_updateFavorites__) {
        __header_updateFavorites__(newFavoriteState);
      }

      dispatch(changeFavorite(newFavoriteState, eventActions));
      if (isFavorite) {
        e.stopPropagation();
      } else {
        motivateAuthenticate(e);
      }

      dispatch(setFavorite(newFavoriteState));
    },
    [dispatch, isFavorite, motivateAuthenticate, eventActions],
  );

  const onMotivationPopupClose = React.useCallback(() => {
    trackAuthenticateMotivationClose();
    setMotivationPopupVisibility(false);
  }, [setMotivationPopupVisibility]);

  return (
    <>
      <FavoriteButton theme={buttonTheme} onClick={onFavoriteButtonClick} isFavorite={isFavorite} />
      {isMotivationPopupOpened && (
        <ModalWindow open escape outside onClose={onMotivationPopupClose}>
          <AuthenticateMotivation url={getRequestUrl(path)} onAuthLinkClick={trackAuthenticateMotivationLinkClick} />
        </ModalWindow>
      )}
    </>
  );
}
